import { useState } from 'react'

import {
  AvatarStack,
  Box,
  Icon,
  Tag,
  Text,
  Tooltip,
  useToggle,
} from '@factorialco/deprecated-design-system'

import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'

import SelectAdditionalSignersModal from 'modules/documents/components/BulkFiles/DocumentAssigner/SummaryWidget/SelectAdditionalSignersModal'
import { UploadProcess } from 'modules/documents/hooks/useGetUploadProcess'
import { UploadProcessDocument } from 'modules/documents/hooks/useGetUploadProcessDocuments'

interface Props {
  uploadProcess: UploadProcess
  uploadProcessDocument?: UploadProcessDocument
  onSignersUpdateFinished?: () => void
}

const MAX_VISIBLE_SIGNERS = 3

const SummaryWidget = ({
  uploadProcess,
  uploadProcessDocument,
  onSignersUpdateFinished,
}: Props) => {
  const [open, toggle] = useToggle()
  const [additionalSignersModalVisible, setAdditionalSignersModalVisible] = useState(false)
  const [leftOver, setLeftOver] = useState<number>(0)

  const signers = useUnsafeMemo(() => {
    const additionalSigners =
      uploadProcessDocument?.additionalSigners || uploadProcess?.additionalSigners || []

    setLeftOver(additionalSigners.length - MAX_VISIBLE_SIGNERS)
    return additionalSigners?.slice(0, MAX_VISIBLE_SIGNERS).map((signer) => {
      return {
        label: signer.fullName,
        url: buildUrl(signer.avatar ?? ''),
      } as const
    })
  }, [uploadProcessDocument?.additionalSigners])

  const canSeeAdditionalSigners = signers && signers.length > 0
  const canEditAdditionalSigners = uploadProcess.esignatureRequest && onSignersUpdateFinished

  return (
    <Box gap="s8" flexDirection="column">
      <Box flexDirection="row">
        <Tag type="viridian">{i18n.t('split_documents.summary_widget.esignature_requested')}</Tag>
      </Box>
      <Box flexDirection="row" alignItems="center">
        <Text size="200" weight="medium" color="grey1000">
          {i18n.t('split_documents.summary_widget.recipient')}
        </Text>
        <Box alignItems="flexEnd" width="full">
          <Icon.Medium icon="check" color="viridian1200" />
        </Box>
      </Box>
      {canSeeAdditionalSigners && (
        <Box flexDirection="row" alignItems="center">
          <Box flexDirection="row" alignItems="center" width="full-view-height">
            <Text size="200" weight="medium" color="grey1000">
              {i18n.t('split_documents.summary_widget.additional_signers')}
            </Text>
            {canEditAdditionalSigners ? (
              <Tooltip.CallToAction
                attachment="bottom center"
                open={open}
                toggle={toggle}
                description={i18n.t('split_documents.summary_widget.tooltip')}
                buttonLabel={i18n.t('split_documents.summary_widget.change_additional_signers')}
                onClick={() => setAdditionalSignersModalVisible(true)}
              >
                <Icon.Medium icon="info-circle-line" color="iconSecondary" />
              </Tooltip.CallToAction>
            ) : (
              <Tooltip.Dialog
                attachment="bottom center"
                open={open}
                toggle={toggle}
                label={i18n.t('split_documents.summary_widget.tooltip')}
              >
                <Icon.Medium icon="info-circle-line" color="iconSecondary" />
              </Tooltip.Dialog>
            )}
          </Box>
          <Box alignItems="flexEnd">
            <AvatarStack
              size="s40"
              overlap="s12"
              avatars={signers}
              leftover={leftOver}
              textSize={'200'}
              textColor={'grey000White'}
              backgroundColor={'champagne1000'}
            />
          </Box>
        </Box>
      )}
      {additionalSignersModalVisible && (
        <SelectAdditionalSignersModal
          uploadProcessDocument={uploadProcessDocument}
          onClose={() => setAdditionalSignersModalVisible(false)}
          onSignersUpdateFinished={onSignersUpdateFinished}
        />
      )}
    </Box>
  )
}

export default SummaryWidget
